import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Input } from 'antd';
import { Row, Col } from 'reactstrap';
import { LuDownload } from 'react-icons/lu';
import moment from 'moment';
import SurveyReportApi from '../../../Api/SurveyReportApi';
import TmMasterTable from '../../ReusableComponents/TmMasterTable';
import TmFilter from '../../ReusableComponents/TmFilter';
import Loader from '../../ReusableComponents/Loader';
import { downloadSurveyReportExcel } from '../../ReusableComponents/downloadSurveyExcel';
import { surveyReportHeader } from '../../../constant/tableHeader';
import {
  MONTHS_INDEXZ,
  SURVEY_COMPLETION_STATUS,
} from '../../../constant/constant';
import { generateYears } from '../../../ReusableFn/reusableFn';
import { toastWarn } from '../../Toast/ToastComponent';

const { Search } = Input;
export const SurveyReportComp = () => {
  const debounceTimeoutRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(
    searchParams.get('search') || '',
  );
  const isInitialVisit = [...searchParams].length === 0;
  const getInitialMonth = () => {
    if (searchParams.has('month')) {
      const val = searchParams.get('month');
      return val === '' ? undefined : Number(val);
    }
    return moment().month() + 1;
  };
  const getInitialYear = () => {
    if (searchParams.has('year')) {
      const val = searchParams.get('year');
      return val === '' ? undefined : Number(val);
    }
    return isInitialVisit ? moment().year() : undefined;
  };
  const [filterValue, setFilterValue] = useState({
    month: getInitialMonth(),
    year: getInitialYear(),
    completion_status: searchParams.get('completion_status') || '',
    search: searchParams.get('search') || '',
  });
  const currentMonth = moment().month() + 1;
  const filterArray = [
    {
      options: MONTHS_INDEXZ,
      optionFilterProp: 'label',
      placeholder: 'Month',
      controlName: 'month',
      allowClear: false,
      input: 'select',
      col: 2,
      defaultValue:
        filterValue.month !== undefined && filterValue.month !== null
          ? Number(filterValue.month)
          : currentMonth,
    },
    {
      options: generateYears() || [],
      optionFilterProp: 'label',
      placeholder: 'Year',
      controlName: 'year',
      allowClear: true,
      input: 'select',
      col: 2,
      defaultValue:
        filterValue.year ?? (isInitialVisit ? moment().year() : undefined),
    },
    {
      options: SURVEY_COMPLETION_STATUS,
      optionFilterProp: 'label',
      placeholder: 'Completion Status',
      controlName: 'completion_status',
      allowClear: true,
      input: 'select',
      col: 2,
      defaultValue: filterValue.completion_status || undefined,
    },
    {
      order: 1,
      col: 'auto',
      className: 'ms-auto pe-0',
      input: 'button',
      type: 'dashed',
      htmlType: 'button',
      text: 'Reset',
    },
  ];
  const processRows = (data) =>
    data.map((row, idx) => ({
      ...row,
      serialNo: idx + 1,
      no_of_questions:
        row.no_of_questions ?? row.question_count ?? row.totalQuestions ?? '--',
      period: row.period ?? row.period_name ?? '--',
      start_date: row.start_date ?? row.startDate ?? '--',
      end_date: row.end_date ?? row.endDate ?? '--',
      totalAssigned:
        row.totalAssigned === 0 || row.totalAssigned === '0'
          ? '0'
          : row.totalAssigned || '--',
      totalAttended:
        row.totalAttended === 0 || row.totalAttended === '0'
          ? '0'
          : row.totalAttended || '--',
      totalNotAttended:
        row.totalNotAttended === 0 || row.totalNotAttended === '0'
          ? '0'
          : row.totalNotAttended || '--',
    }));
  const applySearch = (data, search) => {
    if (!search || !search.trim()) return data;
    const lower = search.toLowerCase();
    return data.filter((row) =>
      Object.values(row).some((val) =>
        String(val ?? '')
          .toLowerCase()
          .includes(lower),
      ),
    );
  };
  const navigate = useNavigate();
  const fetchSurveyReports = useCallback(
    async (filters = {}) => {
      try {
        setLoading(true);
        const payload = {
          ...filters,
          limit: 100000,
          offset: 0,
        };
        const response = await SurveyReportApi.getSurveyReportList(payload);
        if (response?.data?.success) {
          const processed = processRows(response.data.result || []);
          setAllRows(processed);
          setRows(applySearch(processed, filters.search || searchValue));
        }
      } catch (error) {
        console.error('Survey Report Error:', error);
      } finally {
        setLoading(false);
      }
    },
    [searchValue],
  );

  const onReportRowClick = (action, row) => {
    if (action !== 'View') return;
    const surveyId = row?.survey_id || row?.surveyId || row?.id;
    if (!surveyId) return;
    navigate(`/survey_report/${btoa(String(surveyId))}/base_users`);
  };

  const handleFilterChange = (obj) => {
    if (obj.submit) return;
    let modifications = { ...obj };
    const rawMonth = modifications.month;
    if (rawMonth === undefined || rawMonth === null || rawMonth === '') {
      modifications.month = moment().month() + 1;
    } else if (!isNaN(Number(rawMonth))) {
      modifications.month = Number(rawMonth);
    } else {
      const parsed = moment().month(rawMonth).month() + 1;
      modifications.month = !isNaN(parsed) ? parsed : moment().month() + 1;
    }
    const updatedFilter = { ...filterValue, ...modifications };
    setFilterValue(updatedFilter);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('pageNumber', 1);
        next.set('month', updatedFilter.month);
        if (updatedFilter.year !== undefined && updatedFilter.year !== null) {
          next.set('year', String(updatedFilter.year));
        } else {
          next.delete('year');
        }
        if (updatedFilter.completion_status) {
          next.set('completion_status', updatedFilter.completion_status);
        } else {
          next.delete('completion_status');
        }
        return next;
      },
      { replace: true },
    );
  };
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value.trim()) {
          next.set('search', value.trim());
        } else {
          next.delete('search');
        }
        next.set('pageNumber', 1);
        return next;
      },
      { replace: true },
    );
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      const trimmed = value.trim();
      const newFilter = { ...filterValue, search: trimmed };
      setFilterValue(newFilter);
    }, 400);
  };
  const handleDownload = async () => {
    if (!rows || rows.length === 0) {
      toastWarn('No data available to download');
      return;
    }
    await downloadSurveyReportExcel(rows, 'Survey_Report.xlsx');
  };
  useEffect(() => {
    fetchSurveyReports(filterValue);
  }, [fetchSurveyReports, filterValue]);

  return (
    <div>
      <div className="d-md-flex">
        <div>
          <h5 className="text-nowrap me-2">Survey Report</h5>
        </div>
        <div className="ms-md-auto">
          <div className="d-md-flex">
            <div className="me-md-2">
              <Search
                allowClear
                placeholder="Search"
                value={searchValue}
                onChange={handleSearch}
                onClear={() => {
                  setSearchValue('');
                  const newFilter = { ...filterValue, search: '' };
                  setFilterValue(newFilter);
                  fetchSurveyReports(newFilter);
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('search');
                    next.set('pageNumber', 1);
                    return next;
                  });
                }}
              />
            </div>
            <div className="mt-2 d-flex justify-content-between mt-md-0">
              <div>
                <Button
                  type="dashed"
                  className="me-1"
                  htmlType="button"
                  onClick={handleDownload}
                >
                  <span>
                    <LuDownload size={18} />
                  </span>
                  Download
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Row>
        <Col>
          <div className="mt-2">
            <TmFilter
              filterArray={filterArray}
              handleFilterChange={handleFilterChange}
              clearDatesOnReset={false}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          {loading ? (
            <Loader />
          ) : (
            <TmMasterTable
              rows={rows}
              columns={surveyReportHeader}
              custom={[]}
              toggleModal={onReportRowClick}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};
